import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Upload, FolderKanban, BellRing, Camera, CalendarRange, BellDot } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { WalletArt } from "@/components/ww/WalletArt";
import { PricingCards } from "@/components/ww/Pricing";
import { ProductIcon, StatusBadge } from "@/components/ww/Misc";
import { useApp } from "@/lib/app-context";

const BENEFITS = [
  [Upload, "Upload", "Capture your purchase documents in seconds."],
  [FolderKanban, "Organise", "Keep every warranty and guarantee in one place."],
  [BellRing, "Remember", "Get notified before your coverage expires."],
];
const STEPS = [
  [Camera, "Upload your documents", "Photograph your purchase invoice, warranty or guarantee card."],
  [CalendarRange, "Add coverage dates", "Save each warranty or guarantee period for your product."],
  [BellDot, "Get timely reminders", "Stay informed before each coverage period expires."],
];
const DEMO = [
  { name: "Samsung Refrigerator", brand: "Samsung", status: "active", covs: 2, note: "Compressor warranty · 9 yrs left" },
  { name: "HP Laptop", brand: "HP", status: "expiring", covs: 1, note: "Expires in 12 days" },
  { name: "Sony Television", brand: "Sony", status: "expired", covs: 1, note: "Expired 3 months ago" },
];
const FAQS = [
  ["What is Warranty Wallet?", "Warranty Wallet is a digital storage and reminder service for purchase invoices, warranties and guarantees."],
  ["Is Warranty Wallet free?", "Yes. The Free plan supports up to 3 product records."],
  ["Can one product have multiple warranties?", "Yes. One product can contain multiple warranty and guarantee periods."],
  ["Will I receive expiry reminders?", "Yes. The platform schedules reminders before each recorded coverage period expires."],
  ["Can I download my documents?", "Yes. Customers can view and download their own uploaded documents."],
  ["Does Warranty Wallet handle warranty claims?", "No. Warranty Wallet stores documents and provides reminders. Claims must be handled with the relevant warranty provider."],
  ["Can I use Warranty Wallet outside India?", "Yes. International customers can use the Free plan. Paid subscriptions will be introduced country by country."],
  ["What happens when my subscription expires?", "Your existing documents and records remain accessible. Additional product creation is governed by your effective plan limit."],
  ["Does uploading a warranty card activate my warranty?", "No. Uploading documents does not register or activate a manufacturer's warranty."],
];

const SectionHead = ({ eyebrow, title, desc }) => (
  <div className="mx-auto mb-14 max-w-2xl text-center">
    {eyebrow && <p className="ww-eyebrow">{eyebrow}</p>}
    <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ww-navy sm:text-4xl">{title}</h2>
    <p className="mt-4 text-base text-ww-muted md:text-lg">{desc}</p>
  </div>
);

const DemoCard = ({ p, i }) => (
  <div className="ww-reveal rounded-[24px] border border-ww-border bg-white p-6 shadow-[0_24px_50px_-36px_rgba(16,43,64,0.5)] transition-transform duration-300 hover:-translate-y-1" style={{ animationDelay: `${i * 0.12}s` }} data-testid={`demo-product-card-${i}`}>
    <div className="flex items-start justify-between">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ww-mint text-ww-emerald"><ProductIcon name={p.name} /></span>
      <StatusBadge status={p.status} />
    </div>
    <p className="mt-5 font-display text-lg font-semibold text-ww-navy">{p.name}</p>
    <p className="text-sm text-ww-muted">{p.brand} · {p.covs} coverage period{p.covs > 1 ? "s" : ""}</p>
    <p className="mt-4 border-t border-ww-border pt-4 text-sm font-medium text-ww-navy/80">{p.note}</p>
  </div>
);

export default function Home() {
  const { isSignedIn } = useAuth();
  const { openSignup } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [preview, setPreview] = useState(false);
  useEffect(() => {
    if (params.get("signup") && !isSignedIn) { openSignup(); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, isSignedIn]);
  const create = () => (isSignedIn ? navigate("/wallet") : openSignup());
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <main data-testid="home-page">
      <section className="relative overflow-hidden ww-hero-bg">
        <div className="ww-grain" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 pb-20 pt-14 lg:grid-cols-[1.05fr_1fr] lg:px-8 lg:pb-28 lg:pt-20">
          <div className="ww-reveal">
            <p className="ww-eyebrow" data-testid="hero-eyebrow">YOUR DIGITAL WARRANTY COMPANION</p>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-ww-navy sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              Your Purchases.<br /><span className="ww-gradient-text">Beautifully Protected.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ww-muted md:text-lg">A beautiful digital home for your warranties, guarantees and purchase invoices. Keep everything organised and get timely reminders before coverage expires.</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <button onClick={create} className="ww-btn-primary ww-btn-lg justify-center" data-testid="hero-create-wallet-button">Create Your Free Wallet <ArrowRight className="h-4 w-4" /></button>
              <button onClick={() => scrollTo("how")} className="ww-btn-ghost ww-btn-lg justify-center" data-testid="hero-how-it-works-button">Explore How It Works</button>
            </div>
          </div>
          <div className="ww-reveal" style={{ animationDelay: ".15s" }}><WalletArt /></div>
        </div>
      </section>

      <section className="mx-auto -mt-8 max-w-7xl px-5 lg:px-8" aria-label="Benefits">
        <div className="grid gap-5 md:grid-cols-3">
          {BENEFITS.map(([Icon, t, d], i) => (
            <div key={t} className="ww-reveal group rounded-[24px] border border-ww-border bg-white/90 p-7 shadow-[0_24px_50px_-38px_rgba(16,43,64,0.55)] backdrop-blur transition-transform duration-300 hover:-translate-y-1" style={{ animationDelay: `${0.1 * i}s` }} data-testid={`benefit-card-${t.toLowerCase()}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[linear-gradient(140deg,#08A881,#087C65)] text-white shadow-[0_10px_24px_-10px_rgba(8,124,101,0.7)] transition-transform duration-300 group-hover:scale-105"><Icon className="h-5 w-5" /></span>
              <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-ww-emerald">{t}</p>
              <p className="mt-2 text-lg font-semibold leading-snug text-ww-navy">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="how" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 lg:px-8 lg:py-32">
        <SectionHead eyebrow="How it works" title="Simple as 1, 2, 3." desc="From paper slips to organised digital records." />
        <div className="relative grid gap-6 md:grid-cols-3">
          <div className="absolute left-[16%] right-[16%] top-10 hidden h-px bg-[linear-gradient(90deg,transparent,#08A881,transparent)] md:block" />
          {STEPS.map(([Icon, t, d], i) => (
            <div key={t} className="relative rounded-[24px] p-6 text-center" data-testid={`how-step-${i + 1}`}>
              <span className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-[22px] border border-ww-border bg-white text-ww-emerald shadow-[0_18px_40px_-24px_rgba(8,124,101,0.6)]"><Icon className="h-8 w-8" /><span className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-ww-navy text-xs font-bold text-white">{i + 1}</span></span>
              <p className="mt-6 font-display text-xl font-semibold text-ww-navy">{t}</p>
              <p className="mx-auto mt-2 max-w-xs text-sm text-ww-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[linear-gradient(180deg,#EAF9F9,#EAF9F4)] py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHead eyebrow="My Wallet" title="Your products. Beautifully organised." desc="A personal digital home for everything you've purchased." />
          <div className="grid gap-6 md:grid-cols-3">{DEMO.map((p, i) => <DemoCard key={p.name} p={p} i={i} />)}</div>
          <div className="mt-10 text-center"><button onClick={() => setPreview(true)} className="ww-btn-ghost bg-white" data-testid="explore-wallet-preview-button">Explore Wallet Preview <ArrowRight className="h-4 w-4" /></button></div>
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 lg:px-8 lg:py-32">
        <SectionHead eyebrow="Pricing" title="Simple, transparent pricing." desc="Start free. Upgrade when you need more storage." />
        <PricingCards />
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-24 lg:px-8">
        <SectionHead eyebrow="FAQ" title="Questions, answered." desc="Everything you need to know about Warranty Wallet." />
        <Accordion type="single" collapsible className="rounded-[24px] border border-ww-border bg-white px-6" data-testid="faq-accordion">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`} className="border-ww-border last:border-0">
              <AccordionTrigger className="text-left font-semibold text-ww-navy hover:no-underline" data-testid={`faq-question-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-ww-muted">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="px-5 pb-24 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[32px] bg-ww-navy px-8 py-16 text-center sm:px-16">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-ww-bright/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-[#3CCFD2]/20 blur-3xl" />
          <h2 className="relative font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Your purchases deserve a safer place.</h2>
          <p className="relative mt-4 text-base text-white/70 md:text-lg">Everything important, beautifully organised.</p>
          <button onClick={create} className="ww-btn-primary ww-btn-lg relative mt-9 bg-ww-bright" data-testid="final-cta-create-wallet-button">Create Your Free Wallet <ArrowRight className="h-4 w-4" /></button>
        </div>
      </section>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-3xl rounded-[28px] p-7" data-testid="wallet-preview-dialog">
          <DialogTitle className="font-display text-2xl text-ww-navy">Wallet Preview</DialogTitle>
          <DialogDescription>A read-only demonstration. Your real wallet starts empty and only shows your own products.</DialogDescription>
          <div className="grid grid-cols-3 gap-3">
            {[["Saved Products", 3], ["Active Coverage", 2], ["Expired", 1]].map(([l, v]) => <div key={l} className="rounded-2xl bg-ww-mint/70 p-4"><p className="text-xs text-ww-muted">{l}</p><p className="font-display text-2xl font-semibold text-ww-navy">{v}</p></div>)}
          </div>
          <div className="rounded-2xl border border-ww-border p-5">
            <div className="flex items-center justify-between"><p className="font-semibold text-ww-navy">Samsung Refrigerator</p><StatusBadge status="active" /></div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between rounded-xl bg-red-50 px-3 py-2"><span>Comprehensive Warranty · 2 years</span><span className="font-semibold text-red-600">Expired</span></div>
              <div className="flex justify-between rounded-xl bg-ww-mint px-3 py-2"><span>Compressor Warranty · 10 years</span><span className="font-semibold text-ww-emerald">Active</span></div>
            </div>
            <p className="mt-3 text-xs text-ww-muted">The product stays active while any coverage period remains active.</p>
          </div>
          <button onClick={() => { setPreview(false); create(); }} className="ww-btn-primary justify-center" data-testid="wallet-preview-create-button">Create Your Free Wallet</button>
        </DialogContent>
      </Dialog>
    </main>
  );
}
